import {
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {Col, Collapse, Row} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {SowResultsTypes} from "../../../constans/resultsTypes";
import {
    sowResultsAutoRange,
    sowResultSelects,
    sowResultsInfo,
    sowResultsMinMaxFunctions,
} from "../../../constans/sowCardSelects";
import useMultiQueryCallerResource from "../../../hooks/useMultiQueryCallerResource";
import useQuery from "../../../hooks/useQuery";
import useQueryCaller from "../../../hooks/useQueryCaller";
import {hasAnyBreedingLicense} from "../../../selectors/farmSelector";
import {getPercent} from "../../../utils/GaugeUtils";
import {isMobile} from "../../../utils/MobileUtils";
import {getAnimalUnit} from "../../../utils/SettingsUtils";
import {convertWeightUnitTo} from "../../../utils/UnitUtils";
import ErrorBoundary from "../../basics/error-boundary/ErrorBoundary";
import Gauge from "../../basics/gauge/Gauge";
import TableGrid from "../../basics/table-grid/TableGrid";
import DefaultMobileRow from "../../basics/table-grid/default-mobile-row/DefaultMobileRow";
import LoadingComponent from "../../loading/LoadingComponent";
import SkeletonGauge from "../../skeletons/SkeletonGauge";
import CollapsableContainer from "../containers/CollapsableContainer";
import "./_sow-results.scss";

function useMinMax(minMaxFunction, minMaxQuery) {
    return useMemo(() => {
        if (minMaxFunction) return minMaxFunction;
        if (!minMaxQuery || minMaxQuery.ExecCode === "FAIL") {
            return {min: 0, max: 0};
        }
        const max = minMaxQuery.Data[0].val;
        return {min: 0, max};
    }, [minMaxFunction, minMaxQuery]);
}

function GaugeItemFailureRenderer({label}) {
    return (
        <Col xs={6} xl={3} className="d-flex justify-content-center">
            <Gauge label={label} />
        </Col>
    );
}

function GaugeItemSuccessRenderer({
    label,
    min,
    max,
    isSelected,
    reverse,
    valueFormatter,
    sideToSide,
    type,
    onClick: onClickProps,
    value,
}) {
    const percent = useMemo(() => {
        return getPercent(value, min, max, reverse);
    }, [value, min, max, reverse]);

    const onClick = useCallback(() => {
        onClickProps((prev) => (prev !== type ? type : null));
    }, [onClickProps, type]);

    return (
        <Col xs={6} xl={3} className="d-flex justify-content-center">
            <Gauge
                label={label}
                percent={percent}
                min={min}
                max={max}
                onClick={onClick}
                showLegend
                isSelected={isSelected}
                value={value}
                valueFormatter={valueFormatter}
                sideToSide={sideToSide}
            />
        </Col>
    );
}

function GaugeItemRenderer({
    label,
    min,
    max,
    isSelected,
    reverse,
    valueFormatter,
    sideToSide,
    type,
    onClick,
    result,
}) {
    const value = useMemo(() => {
        if (result.ExecCode === "FAIL" || !result.Data[0]) return 0;
        return result.Data[0].val;
    }, [result]);

    if (result.ExecCode === "FAIL" || !result.Data[0]) {
        return <GaugeItemFailureRenderer label={label} />;
    }
    return (
        <GaugeItemSuccessRenderer
            label={label}
            min={min}
            max={max}
            sideToSide={sideToSide}
            reverse={reverse}
            valueFormatter={valueFormatter}
            onClick={onClick}
            type={type}
            isSelected={isSelected}
            value={value}
        />
    );
}

function ParturitionIndexItem({
    onClick,
    selected,
    result,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("newReports.ranking.parturitionIndex")}
            min={min}
            max={max}
            onClick={onClick}
            type={SowResultsTypes.PARTURITION_INDEX}
            isSelected={SowResultsTypes.PARTURITION_INDEX === selected}
            result={result}
        />
    );
}

function HealthyPigletsPerLitterItem({
    result,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("sowResults.healthyPigletsPerLitter")}
            min={min}
            max={max}
            onClick={onClick}
            type={SowResultsTypes.HEALTHY_PIGLETS_PER_LITTER}
            isSelected={SowResultsTypes.HEALTHY_PIGLETS_PER_LITTER === selected}
            result={result}
        />
    );
}

function AverageParturitionWeightItem({
    result,
    valueFormatter,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("sowResults.avgParturitionWeight")}
            min={min}
            max={max}
            valueFormatter={valueFormatter}
            onClick={onClick}
            type={SowResultsTypes.AVG_PARTURITION_WEIGHT}
            isSelected={SowResultsTypes.AVG_PARTURITION_WEIGHT === selected}
            result={result}
        />
    );
}

function SeparatedPigletPerLitterItem({
    result,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("sowResults.separatedPigletsPerLitter")}
            min={min}
            max={max}
            onClick={onClick}
            type={SowResultsTypes.SEPARATED_PIGLETS_PER_LITTER}
            isSelected={
                SowResultsTypes.SEPARATED_PIGLETS_PER_LITTER === selected
            }
            result={result}
        />
    );
}

function AverageSeparatedWeightItem({
    result,
    valueFormatter,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("sowResults.avgSeparationWeight")}
            min={min}
            max={max}
            valueFormatter={valueFormatter}
            onClick={onClick}
            type={SowResultsTypes.AVG_SEPARATION_WEIGHT}
            isSelected={SowResultsTypes.AVG_SEPARATION_WEIGHT === selected}
            result={result}
        />
    );
}

function IdleDaysItem({
    result,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("sowResults.avgIdleDays")}
            min={min}
            max={max}
            onClick={onClick}
            type={SowResultsTypes.AVG_IDLE_DAYS}
            isSelected={SowResultsTypes.AVG_IDLE_DAYS === selected}
            result={result}
        />
    );
}

function AverageLactationDaysItem({
    result,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("sowResults.avgLactationDays")}
            min={min}
            max={max}
            sideToSide
            onClick={onClick}
            type={SowResultsTypes.AVG_LACTATION_DAYS}
            isSelected={SowResultsTypes.AVG_LACTATION_DAYS === selected}
            result={result}
        />
    );
}

function PigletsMortalityItem({
    result,
    valueFormatter,
    onClick,
    selected,
    minMaxFunction,
    minMaxQuery,
}) {
    const {t} = useTranslation();

    const {min, max} = useMinMax(minMaxFunction, minMaxQuery);

    return (
        <GaugeItemRenderer
            label={t("planTypes.pigletsMortality")}
            min={min}
            max={max}
            valueFormatter={valueFormatter}
            onClick={onClick}
            type={SowResultsTypes.PIGLETS_MORTALITY}
            isSelected={SowResultsTypes.PIGLETS_MORTALITY === selected}
            result={result}
        />
    );
}

function IndexInfo({queryCaller, AnmID}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {name: t("sowResults.productiveDays"), field: "productive"},
            {name: t("gestationDays"), field: "gestation"},
            {name: t("sowResults.lactationDays"), field: "lactation"},
            {name: t("idle"), field: "idle"},
        ];
    }, [t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(sowResultsInfo.indexInfo, [
                    AnmID,
                ]);
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function BornPerLitterInfo({queryCaller, AnmID}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {name: t("alive"), field: "born"},
            {name: t("dead"), field: "dead"},
            {name: t("mummies"), field: "mummy"},
            {name: t("weakPiglets"), field: "weak"},
            {name: t("gilts"), field: "gilts"},
        ];
    }, [t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(sowResultsInfo.liveBornInfo, [
                    AnmID,
                ]);
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function BornWeightInfo({queryCaller, AnmID, weightFormatter}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {
                name: t("sowResults.avgParturitionWeight"),
                field: "weight",
                valueFormatter: weightFormatter,
            },
            {name: t("alive"), field: "born"},
        ];
    }, [weightFormatter, t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(sowResultsInfo.bornWeightInfo, [
                    AnmID,
                ]);
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function SeparatePerLitterInfo({queryCaller, AnmID}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {
                name: t("newReports.rankingReport.separatedPiglets"),
                field: "weaned",
            },
            {name: t("alive"), field: "born"},
            {name: t("newReports.rankingReport.fallsAmount"), field: "fall"},
            {name: t("separationToMommy"), field: "fostering"},
        ];
    }, [t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(
                    sowResultsInfo.weanedPigletsInfo,
                    [AnmID]
                );
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function SeparatedWeightInfo({queryCaller, AnmID, weightFormatter}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {
                name: t("sowResults.avgSeparationWeight"),
                field: "weight",
                valueFormatter: weightFormatter,
            },
            {
                name: t("newReports.rankingReport.separatedPiglets"),
                field: "weaned",
            },
            {name: t("activeNipples"), field: "active"},
        ];
    }, [weightFormatter, t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(sowResultsInfo.weanedWeightInfo, [
                    AnmID,
                ]);
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function IdleDaysInfo({queryCaller, AnmID}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {name: t("idle"), field: "idle"},
        ];
    }, [t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(sowResultsInfo.idleDaysInfo, [
                    AnmID,
                ]);
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function LactationDaysInfo({queryCaller, AnmID}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {name: t("sowResults.lactationDays"), field: "lactation"},
            {name: t("daysAsMommy"), field: "mommy"},
        ];
    }, [t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(
                    sowResultsInfo.lactationDaysInfo,
                    [AnmID]
                );
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function PigletMortalityInfo({queryCaller, AnmID}) {
    const {t} = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const mobile = useRef(isMobile());

    const headers = useMemo(() => {
        return [
            {name: t("group"), field: "group"},
            {name: t("printSelectedAnimalsModal.cycle"), field: "cycle"},
            {name: t("alive"), field: "born"},
            {name: t("fallPiglets"), field: "dead"},
        ];
    }, [t]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await queryCaller(
                    sowResultsInfo.pigletMortalityInfo,
                    [AnmID]
                );
                setData(res);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }

        fetchData();
    }, [AnmID, queryCaller]);

    return (
        <div className="position-relative">
            <LoadingComponent isLoading={loading} />
            <TableGrid
                headers={headers}
                data={data}
                showPagination={!mobile.current}
                paginationItems={10}
                scrollOnPageChange={false}
                mobileRow={<DefaultMobileRow />}
            />
        </div>
    );
}

function SowResultsAdditionalInfo({type, AnmID, weightFormatter}) {
    const queryCaller = useQueryCaller();

    return (
        <div className="mt-4">
            {type === SowResultsTypes.PARTURITION_INDEX && (
                <IndexInfo queryCaller={queryCaller} AnmID={AnmID} />
            )}
            {type === SowResultsTypes.HEALTHY_PIGLETS_PER_LITTER && (
                <BornPerLitterInfo queryCaller={queryCaller} AnmID={AnmID} />
            )}
            {type === SowResultsTypes.AVG_PARTURITION_WEIGHT && (
                <BornWeightInfo
                    queryCaller={queryCaller}
                    AnmID={AnmID}
                    weightFormatter={weightFormatter}
                />
            )}
            {type === SowResultsTypes.SEPARATED_PIGLETS_PER_LITTER && (
                <SeparatePerLitterInfo
                    queryCaller={queryCaller}
                    AnmID={AnmID}
                />
            )}
            {type === SowResultsTypes.AVG_SEPARATION_WEIGHT && (
                <SeparatedWeightInfo
                    queryCaller={queryCaller}
                    AnmID={AnmID}
                    weightFormatter={weightFormatter}
                />
            )}
            {type === SowResultsTypes.AVG_IDLE_DAYS && (
                <IdleDaysInfo queryCaller={queryCaller} AnmID={AnmID} />
            )}
            {type === SowResultsTypes.AVG_LACTATION_DAYS && (
                <LactationDaysInfo queryCaller={queryCaller} AnmID={AnmID} />
            )}
            {type === SowResultsTypes.PIGLETS_MORTALITY && (
                <PigletMortalityInfo queryCaller={queryCaller} AnmID={AnmID} />
            )}
        </div>
    );
}

function SowResultsRenderer({resource, AnmID, minMax}) {
    const data = resource.read();

    const [type, setType] = useState(null);

    const animalUnit = useRef(getAnimalUnit());

    const weightFormatter = useCallback((value) => {
        return convertWeightUnitTo(value, {
            unit: animalUnit.current,
            showUnit: true,
            fixed: 1,
        });
    }, []);

    const percentFormatter = useCallback((value) => {
        if (value === null) return "-";
        return `${value.toFixed(2)}%`;
    }, []);

    console.error(data);

    return (
        <>
            <Row>
                <ParturitionIndexItem
                    result={data.animalIndex}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.animalIndex}
                    minMaxQuery={data.animalIndex_autoRange}
                />
                <HealthyPigletsPerLitterItem
                    result={data.averageLiveBorn}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.averageLiveBorn}
                    minMaxQuery={data.averageLiveBorn_autoRange}
                />
                <AverageParturitionWeightItem
                    result={data.averageBornWeight}
                    valueFormatter={weightFormatter}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.averageBornWeight}
                    minMaxQuery={data.averageBornWeight_autoRange}
                />
                <SeparatedPigletPerLitterItem
                    result={data.averageWeanedPiglets}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.averageWeanedPiglets}
                    minMaxQuery={data.averageWeanedPiglets_autoRange}
                />
                <AverageSeparatedWeightItem
                    result={data.averageWeanedWeight}
                    valueFormatter={weightFormatter}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.averageWeanedWeight}
                    minMaxQuery={data.averageWeanedWeight_autoRange}
                />
                <IdleDaysItem
                    result={data.averageIdleDays}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.averageIdleDays}
                    minMaxQuery={data.averageIdleDays_autoRange}
                />
                <AverageLactationDaysItem
                    result={data.averageLactationDays}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.averageLactationDays}
                    minMaxQuery={data.averageLactationDays_autoRange}
                />
                <PigletsMortalityItem
                    result={data.pigletMortality}
                    valueFormatter={percentFormatter}
                    onClick={setType}
                    selected={type}
                    minMaxFunction={minMax.pigletMortality}
                    minMaxQuery={data.pigletMortality_autoRange}
                />
            </Row>
            <Collapse in={type !== null} mountOnEnter unmountOnExit>
                <div>
                    <SowResultsAdditionalInfo
                        type={type}
                        AnmID={AnmID}
                        weightFormatter={weightFormatter}
                    />
                </div>
            </Collapse>
        </>
    );
}

function SowResultsSkeleton() {
    const {t} = useTranslation();

    return (
        <Row>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge
                    label={t("newReports.ranking.parturitionIndex")}
                />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge
                    label={t("sowResults.healthyPigletsPerLitter")}
                />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge label={t("sowResults.avgParturitionWeight")} />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge
                    label={t("sowResults.separatedPigletsPerLitter")}
                />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge label={t("sowResults.avgSeparationWeight")} />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge label={t("sowResults.avgIdleDays")} />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge label={t("sowResults.avgLactationDays")} />
            </Col>
            <Col xs={6} xl={3} className="d-flex justify-content-center">
                <SkeletonGauge label={t("planTypes.pigletsMortality")} />
            </Col>
        </Row>
    );
}

function SowResultsError() {
    const {t} = useTranslation();
    return (
        <div className="d-flex justify-content-center align-items-center flex-column p-3">
            <i className="far fa-exclamation-triangle fa-3x mb-3" />
            <h5>{t("IOT.updateDeviceFailure")}</h5>
        </div>
    );
}

function SowResultsMain() {
    const {t} = useTranslation();

    const query = useQuery();

    const AnmID = useMemo(() => {
        return query.get("documentsAnimal");
    }, [query]);

    const {queries, minMax} = useMemo(() => {
        let queries = [];
        let minMax = {};
        for (let key in sowResultSelects) {
            let sql = sowResultSelects[key];
            const numberOfParameters = sql.split("?").length - 1;
            queries.push({
                SQL: sql,
                ClientQueryID: key,
                Parameters: new Array(numberOfParameters).fill(AnmID),
            });
            const range = sowResultsMinMaxFunctions[key]();
            console.error(sowResultsAutoRange[key], key);
            if (range[0] !== "auto" && range[1] !== "auto") {
                minMax[key] = {min: range[0], max: range[1]};
            } else if (sowResultsAutoRange[key]) {
                queries.push({
                    SQL: sowResultsAutoRange[key],
                    ClientQueryID: `${key}_autoRange`,
                });
            }
        }
        return {queries, minMax};
    }, [AnmID]);

    const resource = useMultiQueryCallerResource(queries);

    return (
        <CollapsableContainer.Card
            className="sow-results"
            header={t("results.results")}
            defaultExpanded={true}>
            <Suspense fallback={<SowResultsSkeleton />}>
                <ErrorBoundary fallbackComponent={SowResultsError}>
                    <SowResultsRenderer
                        resource={resource}
                        minMax={minMax}
                        AnmID={AnmID}
                    />
                </ErrorBoundary>
            </Suspense>
        </CollapsableContainer.Card>
    );
}

export default function SowResults() {
    const hasBreedingLicense = useSelector(hasAnyBreedingLicense);

    if (!hasBreedingLicense) return null;
    return <SowResultsMain />;
}
